// Vollea — Register screen
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoChevronBack } from "react-icons/io5";
import { Btn, Logo } from "../components/ui-vollea";
import PasswordInput from "../components/PasswordInput";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { C, SP, R } from "../lib/theme";

const ROLES = [
  { value: "player", label: "Player" },
  { value: "club_admin", label: "Club Admin" },
  { value: "league_organiser", label: "League Organiser" },
];

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "player", invite_code: "" });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteOnly, setInviteOnly] = useState(true);
  const [invitedRoles, setInvitedRoles] = useState(["club_admin", "league_organiser"]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/invite-status").then((r) => {
      setInviteOnly(!!r.invite_only);
      if (Array.isArray(r.required_roles)) setInvitedRoles(r.required_roles);
    }).catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Only club_admin / league_organiser need a code (backend authoritatively
  // enforces this — the UI just hides the field when it's not needed).
  const codeRequired = inviteOnly && invitedRoles.includes(form.role);

  // Live mismatch indicator — only shown once the confirm field has any text
  // (so we don't yell at the user before they've finished typing the first one).
  const passwordsMatch = form.password === confirmPassword;
  const showMismatch = confirmPassword.length > 0 && !passwordsMatch;

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (!passwordsMatch) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const u = await register(form);
      navigate(u.role === "super_admin" ? "/admin" : "/dashboard");
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const label = { color: C.textMuted, fontSize: 11, fontWeight: 600, letterSpacing: 1, marginBottom: 6, marginTop: SP.md, textTransform: "uppercase", display: "block" };
  const input = { width: "100%", background: C.surface, borderRadius: R.md, border: `1px solid ${C.border}`, color: C.text, padding: `0 ${SP.lg}px`, height: 52, fontSize: 15, outline: "none" };

  return (
    <form onSubmit={submit} style={{ minHeight: "100vh", background: C.bg, padding: `${SP.lg}px ${SP.lg}px 40px` }}>
      <button type="button" onClick={() => navigate("/")} data-testid="register-back" style={{ width: 40, height: 40, color: C.text }}>
        <IoChevronBack size={26} />
      </button>
      <div style={{ display: "flex", justifyContent: "center", margin: `${SP.xl}px 0` }}><Logo size={34} /></div>

      <h1 style={{ color: C.text, fontWeight: 700, fontSize: 26, margin: 0 }}>Create account</h1>
      <p style={{ color: C.textMuted, fontSize: 14, marginTop: 4, marginBottom: SP.xl }}>
        {inviteOnly
          ? "Players sign up free — club admins & league organisers need an invite code."
          : "Open registration."}
      </p>

      <label style={label}>Full name</label>
      <input data-testid="register-name-input" style={input} value={form.name} onChange={upd("name")} required />

      <label style={label}>Email</label>
      <input data-testid="register-email-input" type="email" style={input} value={form.email} onChange={upd("email")} required />

      <label style={label}>Password (min 6)</label>
      <PasswordInput
        value={form.password}
        onChange={upd("password")}
        minLength={6}
        required
        autoComplete="new-password"
        testid="register-password-input"
        toggleTestid="register-password-toggle"
      />

      <label style={label}>Confirm password</label>
      <PasswordInput
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        minLength={6}
        required
        autoComplete="new-password"
        testid="register-confirm-password-input"
        toggleTestid="register-confirm-password-toggle"
        style={showMismatch ? { border: `1px solid ${C.error}` } : undefined}
      />
      {showMismatch ? (
        <p data-testid="register-password-mismatch" style={{ color: C.error, marginTop: 6, fontSize: 12 }}>
          Passwords don't match.
        </p>
      ) : null}

      <label style={label}>I am a</label>
      <select data-testid="register-role-select" style={input} value={form.role} onChange={upd("role")}>
        {ROLES.map((r) => <option key={r.value} value={r.value} style={{ background: C.surface }}>{r.label}</option>)}
      </select>

      {codeRequired && (
        <>
          <label style={label}>Invite code (VOL-XXXXXX)</label>
          <input
            data-testid="register-invite-input"
            style={{ ...input, fontFamily: "monospace", letterSpacing: 2, textTransform: "uppercase" }}
            value={form.invite_code}
            onChange={(e) => setForm({ ...form, invite_code: e.target.value.toUpperCase() })}
            placeholder="VOL-XXXXXX"
            required
          />
          <p style={{ color: C.textDim, fontSize: 11, marginTop: 6 }}>
            Invite codes are only required for club admin & league organiser roles.
          </p>
        </>
      )}

      {err ? <p data-testid="register-error" style={{ color: C.error, marginTop: SP.md, fontSize: 13 }}>{err}</p> : null}

      <div style={{ height: SP.lg }} />
      <Btn label="CREATE ACCOUNT" loading={busy} onClick={submit} testid="register-submit-button" type="submit" />

      <div style={{ display: "flex", alignItems: "center", gap: SP.md, margin: `${SP.lg}px 0` }}>
        <div style={{ flex: 1, height: 1, background: C.divider }} />
        <span style={{ color: C.textDim, fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase" }}>or</span>
        <div style={{ flex: 1, height: 1, background: C.divider }} />
      </div>
      <GoogleSignInButton
        testid="register-google-button"
        label="Continue with Google"
      />
      <p style={{ color: C.textDim, fontSize: 11, marginTop: SP.sm, textAlign: "center" }}>
        Google sign-in creates a Player account — no invite code needed.
      </p>

      <div style={{ marginTop: SP.xl, textAlign: "center" }}>
        <span style={{ color: C.textMuted, fontSize: 14 }}>Already have an account? </span>
        <button type="button" onClick={() => navigate("/login")} data-testid="register-login-link" style={{ color: C.lime, fontSize: 14, fontWeight: 600 }}>Log in</button>
      </div>
    </form>
  );
}
